import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useSpotStore } from '../store/spotStore';
import { useSessionStore } from '../store/sessionStore';
import { useSpotRequestStore, type SpotRequestDetail } from '../store/spotRequestStore';
import * as spotRequestApi from '../api/spotRequests';
import analytics from '../lib/analytics';
import { getWsBaseUrl } from '../lib/wsUrl';

export function useWebSocket() {
  const accessToken  = useAuthStore((s) => s.accessToken);
  const queryClient  = useQueryClient();
  const wsRef        = useRef<WebSocket | null>(null);
  const timerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    if (!accessToken) {
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }

    let destroyed = false;

    function handleMessage(event: string, data: unknown) {
      const { updateSpotStatus, removeSpot } = useSpotStore.getState();
      const { clearSession } = useSessionStore.getState();

      switch (event) {
        case 'spot.status_changed': {
          const d = data as { spotId: string; status: string };
          if (d.status === 'occupied') {
            removeSpot(d.spotId);
          } else {
            updateSpotStatus(d.spotId, d.status);
          }
          // Invalidate queries so spot detail, free spots list, and owner's spots list refetch
          void queryClient.invalidateQueries({ queryKey: ['spot', d.spotId] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          break;
        }
        case 'session.started': {
          const d = data as { spotId: string };
          void queryClient.invalidateQueries({ queryKey: ['spot', d.spotId] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          break;
        }
        case 'session.scheduled': {
          const d = data as { spotId: string; sessionId: string };
          // Invalidate free spots and spot details when a future session is claimed
          void queryClient.invalidateQueries({ queryKey: ['spot', d.spotId] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          void queryClient.invalidateQueries({ queryKey: ['sessions', 'scheduled'] });
          break;
        }
        // Scenario 2: Someone claimed a spot (notification to owner)
        case 'session.claimed_on_my_spot': {
          const d = data as { spotId: string; spotLabel: string; claimerApartment: string };
          void queryClient.invalidateQueries({ queryKey: ['spot', d.spotId] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          break;
        }
        // Scenario 2: User claimed a spot (notification to claimer)
        case 'session.claimed': {
          const d = data as { spotId: string; spotLabel: string; ownerApartment: string };
          // Refetch active session and free spots list
          void queryClient.invalidateQueries({ queryKey: ['session', 'active'] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          break;
        }
        case 'session.ended': {
          const d = data as { sessionId?: string; spotId: string; userId?: string };
          const currentUserId = useAuthStore.getState().user?.id;

          // If this is the current user's session, immediately refetch active sessions
          if (d.userId && d.userId === currentUserId) {
            void queryClient.refetchQueries({ queryKey: ['session', 'active'], type: 'all' });
          } else {
            // For other users' sessions, just invalidate
            void queryClient.invalidateQueries({ queryKey: ['session', 'active'] });
          }

          // Always refetch spot-related queries with a small delay to allow backend to fully process
          setTimeout(() => {
            void queryClient.refetchQueries({ queryKey: ['spots', 'mine'], type: 'all' });
            void queryClient.refetchQueries({ queryKey: ['spots', 'free'], type: 'all' });
            if (d.spotId) {
              void queryClient.refetchQueries({ queryKey: ['spot', d.spotId], type: 'all' });
            }
          }, 200);
          break;
        }
        // Scenario 5: Session ended confirmation (to occupant)
        case 'session.ended_confirmation': {
          const d = data as { spotId: string; spotLabel: string; reason: string };
          void queryClient.invalidateQueries({ queryKey: ['session', 'active'] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          break;
        }
        // Scenario 5: Session ended on my spot (to owner)
        case 'session.ended_on_my_spot': {
          const d = data as { spotId: string; spotLabel: string; occupantApartment: string; reason: string };
          void queryClient.invalidateQueries({ queryKey: ['spot', d.spotId] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          break;
        }
        case 'reclaim.alert_sent': {
          // Show banner immediately with partial data; query refetch will fill in the id
          const { setReclaimAlert } = useSessionStore.getState();
          setReclaimAlert({ sent_at: new Date().toISOString(), acknowledged_at: null });
          void queryClient.invalidateQueries({ queryKey: ['session', 'active'] });
          break;
        }
        case 'reclaim.acknowledged': {
          const d = data as { spotId: string };
          // Invalidate the spot so the owner's SpotDetailPage refetches and shows acknowledged state
          void queryClient.invalidateQueries({ queryKey: ['spot', d.spotId] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          // Also update the occupant's session store if they're viewing their session
          useSessionStore.setState((state) => ({
            activeSession: state.activeSession
              ? {
                  ...state.activeSession,
                  reclaim_alert: state.activeSession.reclaim_alert
                    ? { ...state.activeSession.reclaim_alert, acknowledged_at: new Date().toISOString() }
                    : null,
                }
              : null,
          }));
          break;
        }
        case 'window.created':
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          break;
        case 'window.closed':
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          break;
        // Scenario 4: Availability cancelled (occupant notified)
        case 'availability.cancelled': {
          const d = data as { spotId: string; spotLabel: string; cancellationType: string; ownerApartment: string };
          const { setAvailabilityCancelled } = useSessionStore.getState();
          setAvailabilityCancelled({
            spotId: d.spotId,
            spotLabel: d.spotLabel,
            ownerApartment: d.ownerApartment,
            timestamp: new Date().toISOString(),
          });
          break;
        }
        case 'session.window_warning_15min': {
          const { setWindow15MinWarning } = useSessionStore.getState();
          setWindow15MinWarning(true);
          analytics.trackSessionWindowWarning15min();
          break;
        }
        case 'spot_request.created': {
          // Refetch incoming requests for spot owners
          void queryClient.invalidateQueries({ queryKey: ['spot-requests', 'incoming'] });
          break;
        }
        case 'spot_offer.created': {
          // Requester receives new offer - refetch their request detail to show it
          const d = data as { offerId: string; requestId: string };
          const { myRequest, setMyRequestDetail } = useSpotRequestStore.getState();

          // If this is the requester's request, refetch the full detail
          if (myRequest && myRequest.id === d.requestId) {
            spotRequestApi.getRequestDetail(d.requestId)
              .then((detail: SpotRequestDetail) => {
                setMyRequestDetail(detail);
              })
              .catch((err: any) => console.error('Failed to refetch request detail:', err));
          }
          break;
        }
        case 'spot_offer.accepted': {
          // Session created, spot is now occupied
          const d = data as { offerId: string; spotId: string };
          const { removeSpot } = useSpotStore.getState();
          removeSpot(d.spotId);
          void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          void queryClient.invalidateQueries({ queryKey: ['session', 'active'] });
          analytics.trackSpotOfferAccepted(d.spotId);
          break;
        }
        case 'spot_offer.dismissed': {
          // Offer was dismissed because another was accepted
          const d = data as { offerId: string };
          const { updateOfferStatus } = useSpotRequestStore.getState();
          updateOfferStatus(d.offerId, 'dismissed');
          void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
          break;
        }
        case 'spot_offer.rejected': {
          // Offer was rejected
          const d = data as { offerId: string };
          const { updateOfferStatus } = useSpotRequestStore.getState();
          updateOfferStatus(d.offerId, 'rejected');
          analytics.trackSpotOfferRejected(d.offerId);
          break;
        }
        case 'spot_request.expired': {
          // Request expired
          const d = data as { requestId: string };
          const { clearMyRequest } = useSpotRequestStore.getState();
          clearMyRequest();
          void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
          analytics.trackSpotRequestExpired();
          break;
        }
        case 'spot_request.cancelled': {
          // Request was cancelled
          void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
          analytics.trackSpotRequestCancelled();
          break;
        }
        case 'spot_request.accepted': {
          // Request was fulfilled by someone accepting an offer
          const d = data as { requestId: string };
          void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
          break;
        }
        case 'extension.cancelled': {
          // Extension request was cancelled because session ended
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
          break;
        }
        // Owner ↔ scheduled-claimer negotiation events. Any change forces a
        // refetch of the HomePage banner data — the banner UI reads from
        // ['availability-change-requests', 'mine'].
        case 'availability_change.requested':
        case 'availability_change.accepted':
        case 'availability_change.withdrawn':
        case 'availability_change.superseded': {
          void queryClient.invalidateQueries({ queryKey: ['availability-change-requests', 'mine'] });
          void queryClient.invalidateQueries({ queryKey: ['spots', 'mine'] });
          break;
        }
      }
    }

    function connect() {
      if (destroyed) return;

      const url = `${getWsBaseUrl()}/ws?token=${accessToken}`;
      const ws  = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (reconnectAttemptsRef.current > 0) {
          analytics.trackWsReconnect(reconnectAttemptsRef.current);
        } else {
          analytics.trackWsConnected();
        }
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (e: MessageEvent<string>) => {
        try {
          const msg = JSON.parse(e.data) as { event: string; data: unknown };
          handleMessage(msg.event, msg.data);
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = (e: CloseEvent) => {
        if (!destroyed) {
          analytics.trackWsDisconnected(String(e.code));
          reconnectAttemptsRef.current += 1;
          // Invalidate spots/free to resync, then reconnect
          void queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
          timerRef.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => ws.close();
    }

    connect();

    return () => {
      destroyed = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [accessToken, queryClient]);
}
